import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { PublicConfigService } from '../services/public-config.service';
import { ActiveCompanyService } from '../services/active-company.service';
import { ModuleAccessService } from '../services/module-access.service';

export const authGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  
  // Performance optimization: If user data is already loaded, skip initialization wait
  if (authService.currentUser() && authService.currentUserProfile() && authService.isAuthenticated()) {
    return true;
  }
  
  // Only wait for initialization if we don't have user data yet
  // Use a short timeout to avoid blocking navigation
  const initPromise = authService.waitForInitialization();
  const timeoutPromise = new Promise((resolve) => 
    setTimeout(() => resolve(null), 500) // 500ms max wait
  );
  
  await Promise.race([initPromise, timeoutPromise]);
  
  // Check if user is authenticated
  if (!authService.isAuthenticated()) {
    router.navigate(['/auth/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }
  
  // If user profile is already loaded, allow navigation immediately
  if (authService.currentUser() && authService.currentUserProfile()) {
    return true;
  }
  
  // Only load profile if missing (token might be valid but profile not loaded yet)
  // Load in background to avoid blocking navigation
  if (!authService.currentUser()) {
    try {
      // Load profile but don't block navigation if it takes too long
      const profilePromise = authService.loadUserProfile();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Profile load timeout')), 1000) // Reduced to 1 second
      );
      
      try {
        await Promise.race([profilePromise, timeoutPromise]);
      } catch (error) {
        // If profile load fails or times out, check if we still have a valid token
        // Allow navigation if token exists (profile will load in background)
        console.warn('Profile load failed or timed out, allowing navigation with token:', error);
      }
      
      // Final check after attempting to load
      if (!authService.isAuthenticated()) {
        router.navigate(['/auth/login'], { queryParams: { returnUrl: state.url } });
        return false;
      }
    } catch (error) {
      // Token validation failed
      router.navigate(['/auth/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }
  }
  
  return true;
};

export const adminGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  
  // Wait for auth initialization
  await authService.waitForInitialization();
  
  if (!authService.isAuthenticated()) {
    router.navigate(['/auth/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }
  
  // If user profile is already loaded, check admin status immediately
  if (authService.currentUser() && authService.currentUserProfile()) {
    if (!authService.isSuperAdmin()) {
      router.navigate(['/home']);
      return false;
    }
    return true;
  }
  
  // Load profile if missing (with timeout to avoid blocking)
  if (!authService.currentUser()) {
    try {
      const profilePromise = authService.loadUserProfile();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Profile load timeout')), 2000)
      );
      
      try {
        await Promise.race([profilePromise, timeoutPromise]);
      } catch (error) {
        console.warn('Profile load failed or timed out:', error);
      }
      
      if (!authService.isAuthenticated()) {
        router.navigate(['/auth/login'], { queryParams: { returnUrl: state.url } });
        return false;
      }
    } catch (error) {
      router.navigate(['/auth/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }
  }
  
  if (!authService.isSuperAdmin()) {
    router.navigate(['/home']);
    return false;
  }
  
  return true;
};

export const companyOwnerGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const moduleAccess = inject(ModuleAccessService);
  const activeCompany = inject(ActiveCompanyService);
  const router = inject(Router);

  await authService.waitForInitialization();

  if (!authService.isAuthenticated()) {
    router.navigate(['/auth/login'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  if (!authService.currentUser()) {
    try {
      await Promise.race([
        authService.loadUserProfile(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Profile load timeout')), 2000)),
      ]);
    } catch (error) {
      console.warn('Profile load failed or timed out:', error);
    }
    if (!authService.isAuthenticated()) {
      router.navigate(['/auth/login'], { queryParams: { returnUrl: state.url } });
      return false;
    }
  }

  activeCompany.loadFromStorage();
  try {
    await activeCompany.refreshCompaniesAndEnsureActive();
    await moduleAccess.refreshFromActiveCompany();
  } catch {
    // best-effort
  }

  if (authService.isSuperAdmin() || authService.isCompanyOwner() || moduleAccess.isActiveCompanyOwner()) {
    return true;
  }

  router.navigate(['/home']);
  return false;
};

export const registerGuard: CanActivateFn = async () => {
  const pub = inject(PublicConfigService);
  const router = inject(Router);
  await pub.load();
  if (!pub.allowPublicRegistration()) {
    await router.navigate(['/auth/login']);
    return false;
  }
  return true;
};
